import React, { PropsWithChildren, useEffect, useLayoutEffect } from 'react';
import { configure } from 'mobx';
import {
    init,
    setTag,
    globalHandlersIntegration,
    thirdPartyErrorFilterIntegration
} from '@sentry/browser';
import { LocaleProvider, Message, Spin } from '@fx-ui/fine-design';
import { ConfigProvider } from '@fx-ui/jdy-design-biz';
import { ConfigProvider as PlatformSdkConfigProvider, CorpThemeColor } from '@fx-ui/platform-sdk';
import { ConfigProvider as OpenSdkConfigProvider } from '@fx-ui/open-sdk';
import Intl, { LOCALE_KEY } from '@fx-ui/intl';
import { ErrorBoundaryFallback, SpanText, UiErrorBoundary } from '~/components';
import { enableES5 } from 'immer';
import { PageContext, RegionContext } from '~/contexts';
import { LocaleRecord } from '~/interfaces';
import {
    configGlobalTheme,
    filter,
    getCsrfToken,
    getDefaultLocaleKey,
    getServerRegion,
    getSuiteName,
    ILocales,
    isString,
    logger,
    tracker
} from '~/utils'; // iconfont图标资源
import iconfontWoff from '~/assets/font/iconfont.woff';
import iconfontTtf from '~/assets/font/iconfont.ttf';

import BIZ_ZH_CN from '@fx-ui/jdy-design-biz/locale/zh_CN';
import BIZ_ZH_TW from '@fx-ui/jdy-design-biz/locale/zh_TW';
import BIZ_EN_US from '@fx-ui/jdy-design-biz/locale/en_US';
import BIZ_JA_JP from '@fx-ui/jdy-design-biz/locale/ja_JP';
import BIZ_VI_VN from '@fx-ui/jdy-design-biz/locale/vi_VN';
import BIZ_RU_RU from '@fx-ui/jdy-design-biz/locale/ru_RU';
import BIZ_FR_FR from '@fx-ui/jdy-design-biz/locale/fr_FR';
import BIZ_ES_ES from '@fx-ui/jdy-design-biz/locale/es_ES';
import BIZ_TH_TH from '@fx-ui/jdy-design-biz/locale/th_TH';
import BIZ_ID_ID from '@fx-ui/jdy-design-biz/locale/id_ID';
import BIZ_KO_KR from '@fx-ui/jdy-design-biz/locale/ko_KR';
import BIZ_DE_DE from '@fx-ui/jdy-design-biz/locale/de_DE';
import BIZ_PT_PT from '@fx-ui/jdy-design-biz/locale/pt_PT';
import BIZ_KM_KH from '@fx-ui/jdy-design-biz/locale/km_KH';
import BIZ_LO_LA from '@fx-ui/jdy-design-biz/locale/lo_LA';
import BIZ_HI_IN from '@fx-ui/jdy-design-biz/locale/hi_IN';
import BIZ_BN_BD from '@fx-ui/jdy-design-biz/locale/bn_BD';
import BIZ_TA_IN from '@fx-ui/jdy-design-biz/locale/ta_IN';
import BIZ_UZ_UZ from '@fx-ui/jdy-design-biz/locale/uz_UZ';
import BIZ_MN_MN from '@fx-ui/jdy-design-biz/locale/mn_MN';
import BIZ_IT_IT from '@fx-ui/jdy-design-biz/locale/it_IT';
import BIZ_NL_NL from '@fx-ui/jdy-design-biz/locale/nl_NL';
import BIZ_PL_PL from '@fx-ui/jdy-design-biz/locale/pl_PL';
import BIZ_HU_HU from '@fx-ui/jdy-design-biz/locale/hu_HU';
import BIZ_PT_BR from '@fx-ui/jdy-design-biz/locale/pt_BR';

import PLATFORM_SDK_ZH_CN from '@fx-ui/platform-sdk/locales/zh_cn.json';
import PLATFORM_SDK_ZH_TW from '@fx-ui/platform-sdk/locales/zh_tw.json';
import PLATFORM_SDK_EN_US from '@fx-ui/platform-sdk/locales/en_us.json';
import PLATFORM_SDK_JA_JP from '@fx-ui/platform-sdk/locales/ja_jp.json';
import PLATFORM_SDK_VI_VN from '@fx-ui/platform-sdk/locales/vi_vn.json';
import PLATFORM_SDK_RU_RU from '@fx-ui/platform-sdk/locales/ru_ru.json';
import PLATFORM_SDK_FR_FR from '@fx-ui/platform-sdk/locales/fr_fr.json';
import PLATFORM_SDK_ES_ES from '@fx-ui/platform-sdk/locales/es_es.json';
import PLATFORM_SDK_TH_TH from '@fx-ui/platform-sdk/locales/th_th.json';
import PLATFORM_SDK_ID_ID from '@fx-ui/platform-sdk/locales/id_id.json';
import PLATFORM_SDK_KO_KR from '@fx-ui/platform-sdk/locales/ko_kr.json';
import PLATFORM_SDK_DE_DE from '@fx-ui/platform-sdk/locales/de_de.json';
import PLATFORM_SDK_PT_PT from '@fx-ui/platform-sdk/locales/pt_pt.json';
import PLATFORM_SDK_KM_KH from '@fx-ui/platform-sdk/locales/km_kh.json';
import PLATFORM_SDK_LO_LA from '@fx-ui/platform-sdk/locales/lo_la.json';
import PLATFORM_SDK_HI_IN from '@fx-ui/platform-sdk/locales/hi_in.json';
import PLATFORM_SDK_BN_BD from '@fx-ui/platform-sdk/locales/bn_bd.json';
import PLATFORM_SDK_TA_IN from '@fx-ui/platform-sdk/locales/ta_in.json';
import PLATFORM_SDK_UZ_UZ from '@fx-ui/platform-sdk/locales/uz_uz.json';
import PLATFORM_SDK_MN_MN from '@fx-ui/platform-sdk/locales/mn_mn.json';
import PLATFORM_SDK_IT_IT from '@fx-ui/platform-sdk/locales/it_it.json';
import PLATFORM_SDK_NL_NL from '@fx-ui/platform-sdk/locales/nl_nl.json';
import PLATFORM_SDK_PL_PL from '@fx-ui/platform-sdk/locales/pl_pl.json';
import PLATFORM_SDK_HU_HU from '@fx-ui/platform-sdk/locales/hu_hu.json';
import PLATFORM_SDK_PT_BR from '@fx-ui/platform-sdk/locales/pt_br.json';

import useIntl from '~/hooks/useIntl';
import FINE_DESIGN_LOCALE from '~/locales/fine-design';
import { FALLBACK_REGION, SuiteId } from '~/constants'; // mobx配置
import { corpUserStore, jsSdkStore } from '~/models';
import { useComponentConstructor } from '@libs/shared';
import { observer } from 'mobx-react-lite';
import { getBrandInfo } from '~/utils/brand';
import siteRouterManager from '~/utils/site-router';

// mobx配置
configure({ enforceActions: 'observed', useProxies: 'never' });
// immer配置
enableES5();

interface IPageProviderProps {
    pageTag: string; // 页面唯一名称
    extraLocales?: ILocales | ILocales[]; // 额外的国际化
}

const JDY_DESIGN_BIZ_LOCALE: LocaleRecord<typeof BIZ_ZH_CN> = {
    [LOCALE_KEY.ZH_CN]: BIZ_ZH_CN,
    [LOCALE_KEY.ZH_TW]: BIZ_ZH_TW,
    [LOCALE_KEY.EN_US]: BIZ_EN_US,
    [LOCALE_KEY.JA_JP]: BIZ_JA_JP,
    [LOCALE_KEY.VI_VN]: BIZ_VI_VN,
    [LOCALE_KEY.RU_RU]: BIZ_RU_RU,
    [LOCALE_KEY.FR_FR]: BIZ_FR_FR,
    [LOCALE_KEY.ES_ES]: BIZ_ES_ES,
    [LOCALE_KEY.TH_TH]: BIZ_TH_TH,
    [LOCALE_KEY.ID_ID]: BIZ_ID_ID,
    [LOCALE_KEY.KO_KR]: BIZ_KO_KR,
    [LOCALE_KEY.DE_DE]: BIZ_DE_DE,
    [LOCALE_KEY.PT_PT]: BIZ_PT_PT,
    [LOCALE_KEY.KM_KH]: BIZ_KM_KH,
    [LOCALE_KEY.LO_LA]: BIZ_LO_LA,
    [LOCALE_KEY.HI_IN]: BIZ_HI_IN,
    [LOCALE_KEY.BN_BD]: BIZ_BN_BD,
    [LOCALE_KEY.TA_IN]: BIZ_TA_IN,
    [LOCALE_KEY.UZ_UZ]: BIZ_UZ_UZ,
    [LOCALE_KEY.MN_MN]: BIZ_MN_MN,
    [LOCALE_KEY.IT_IT]: BIZ_IT_IT,
    [LOCALE_KEY.NL_NL]: BIZ_NL_NL,
    [LOCALE_KEY.PL_PL]: BIZ_PL_PL,
    [LOCALE_KEY.HU_HU]: BIZ_HU_HU,
    [LOCALE_KEY.PT_BR]: BIZ_PT_BR
};

const PLATFORM_SDK_LOCALE: LocaleRecord<any> = {
    [LOCALE_KEY.ZH_CN]: PLATFORM_SDK_ZH_CN,
    [LOCALE_KEY.ZH_TW]: PLATFORM_SDK_ZH_TW,
    [LOCALE_KEY.EN_US]: PLATFORM_SDK_EN_US,
    [LOCALE_KEY.JA_JP]: PLATFORM_SDK_JA_JP,
    [LOCALE_KEY.VI_VN]: PLATFORM_SDK_VI_VN,
    [LOCALE_KEY.RU_RU]: PLATFORM_SDK_RU_RU,
    [LOCALE_KEY.FR_FR]: PLATFORM_SDK_FR_FR,
    [LOCALE_KEY.ES_ES]: PLATFORM_SDK_ES_ES,
    [LOCALE_KEY.TH_TH]: PLATFORM_SDK_TH_TH,
    [LOCALE_KEY.ID_ID]: PLATFORM_SDK_ID_ID,
    [LOCALE_KEY.KO_KR]: PLATFORM_SDK_KO_KR,
    [LOCALE_KEY.DE_DE]: PLATFORM_SDK_DE_DE,
    [LOCALE_KEY.PT_PT]: PLATFORM_SDK_PT_PT,
    [LOCALE_KEY.KM_KH]: PLATFORM_SDK_KM_KH,
    [LOCALE_KEY.LO_LA]: PLATFORM_SDK_LO_LA,
    [LOCALE_KEY.HI_IN]: PLATFORM_SDK_HI_IN,
    [LOCALE_KEY.BN_BD]: PLATFORM_SDK_BN_BD,
    [LOCALE_KEY.TA_IN]: PLATFORM_SDK_TA_IN,
    [LOCALE_KEY.UZ_UZ]: PLATFORM_SDK_UZ_UZ,
    [LOCALE_KEY.MN_MN]: PLATFORM_SDK_MN_MN,
    [LOCALE_KEY.IT_IT]: PLATFORM_SDK_IT_IT,
    [LOCALE_KEY.NL_NL]: PLATFORM_SDK_NL_NL,
    [LOCALE_KEY.PL_PL]: PLATFORM_SDK_PL_PL,
    [LOCALE_KEY.HU_HU]: PLATFORM_SDK_HU_HU,
    [LOCALE_KEY.PT_BR]: PLATFORM_SDK_PT_BR
};

const initSentry = (pageTag?: string) => {
    const dsn = window.jdy_config?.corp_sentry?.pc?.dsn;
    if (dsn) {
        const allowUrls = [
            /jiandaoyun\.com/,
            /jdydevelop\.com/,
            /jdycdn\.com/,
            /finecloud\.com/,
            /frjdy\.com/
        ];
        init({
            dsn,
            allowUrls: allowUrls,
            denyUrls: [/jdy-chart/],
            release: window.jdy_config?.version,
            sampleRate: window.jdy_config?.corp_sentry?.pc?.sample_ratio,
            integrations: function (defaultIntegrations) {
                return [
                    ...filter(defaultIntegrations, (item) => item.name !== 'BrowserSession'),
                    globalHandlersIntegration({ onerror: true, onunhandledrejection: false }),
                    thirdPartyErrorFilterIntegration({
                        filterKeys: ['fx-corp-web-pc'],
                        behaviour: 'drop-error-if-exclusively-contains-third-party-frames'
                    })
                ];
            },
            // 新增g2引擎内部报错屏蔽
            ignoreErrors: [
                /WeixinJSBridge/,
                /Network Error/,
                /Request failed/,
                /ResizeObserver loop/,
                /reading 'destroyed'/,
                /unhandledrejection/
            ]
        });
        // 设置sentry的标签，方便识别异常对应页面
        if (isString(pageTag)) {
            setTag('page', pageTag);
        }
    }
};

const initMessageConfig = () => {
    Message.config({
        maxCount: {
            toast: 3
        }
    });
};

const initFontStyle = () => {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = ` @font-face {
                font-family: 'fx-pc';
                src: url(${iconfontWoff}) format('woff'), url(${iconfontTtf}) format('truetype');
            }
            `;
    const head = document.getElementsByTagName('head').item(0);
    if (head) {
        head.appendChild(style);
    }
};

const region = getServerRegion() || FALLBACK_REGION;

const PageProvider: React.FC<PropsWithChildren<IPageProviderProps>> = ({
    pageTag,
    extraLocales,
    children
}) => {
    /**
     * 1、国际化预设值
     * 2、初始化业务国际化
     */
    useComponentConstructor(() => {
        /**
         * fx-code 渲染的 pug，productName = jdy
         * fx-corp 渲染的 pug，productName = fr
         */
        const productName = getBrandInfo().brandName;
        // 应用搭建
        const appBuilderName = getSuiteName(SuiteId.APP_BUILDER);
        // 九数云
        const dataAnalyticsName = getSuiteName(SuiteId.ANALYTICS);
        // 悟帆
        const wufanName = getSuiteName(SuiteId.WUFAN);

        // 设置预设值
        // 不能放到utils或者hooks,这两个目录只支持单向引入
        Intl.setDefaultValues({
            get: {
                productName,
                appBuilderName,
                dataAnalyticsName,
                wufanName
            },
            getHTML: {
                productName,
                appBuilderName,
                dataAnalyticsName,
                wufanName,
                SpanError: (s: string) => <SpanText type="error">{s}</SpanText>,
                SpanWarning: (s: string) => <SpanText type="warning">{s}</SpanText>,
                SpanSuccess: (s: string) => <SpanText type="success">{s}</SpanText>,
                SpanPrimary: (s: string) => <SpanText type="primary">{s}</SpanText>,
                SpanInfo: (s: string) => <SpanText type="info">{s}</SpanText>,
                Strong: (s: string) => <strong>{s}</strong>,
                Br: <br />
            }
        });
    });

    const [initialized, init, currentLocale, initDayjs] = useIntl(extraLocales, region);
    /**
     * 处理pug注入企业主题样式
     */
    useLayoutEffect(() => {
        configGlobalTheme({
            themeEnabled: !!window.jdy_corp_theme?.enabled,
            themeColor: window.jdy_corp_theme?.color as CorpThemeColor
        });
    }, []);

    useEffect(() => {
        // 初始化代码错误监控
        initSentry(pageTag);
        // 初始化最大消息数量
        initMessageConfig();
        initFontStyle();
        logger.setBizContext({
            pageId: pageTag
        });
        jsSdkStore.init();

        return () => {
            logger.close();
        };
    }, []);

    if (!initialized) {
        return <Spin isFullScreen />;
    }

    const basePath = window.jdy_config.base_path || '';

    return (
        <PageContext.Provider value={{ initLocale: init, initDayjs }}>
            <RegionContext.Provider value={region}>
                <LocaleProvider locale={FINE_DESIGN_LOCALE[currentLocale]} key={currentLocale}>
                    <ConfigProvider locale={JDY_DESIGN_BIZ_LOCALE[currentLocale]}>
                        <PlatformSdkConfigProvider
                            h5JsApiReady={jsSdkStore.h5JsApiReady}
                            externalUserBeta={window.jdy_config.external_beta}
                            suiteId={undefined}
                            locale={PLATFORM_SDK_LOCALE[currentLocale]}
                            localeKey={currentLocale}
                            defaultLocaleKey={getDefaultLocaleKey()}
                            origin={basePath}
                            // 管理后台固定使用平台域名访问，临时参数，待 jdy 支持跨域调平台后移除 todo skye
                            platformSite={siteRouterManager.getPlatformSiteWithBasePath()}
                            csrfToken={getCsrfToken()}
                            tracker={tracker}
                        >
                            <OpenSdkConfigProvider
                                site={window.jdy_config.site}
                                locale={currentLocale}
                                basePath={basePath}
                                tracker={tracker}
                                authModel={corpUserStore}
                            >
                                <UiErrorBoundary
                                    fallbackComponent={(props) => (
                                        <ErrorBoundaryFallback
                                            {...props}
                                            onRefresh={() => <>{children}</>}
                                        />
                                    )}
                                >
                                    {children}
                                </UiErrorBoundary>
                            </OpenSdkConfigProvider>
                        </PlatformSdkConfigProvider>
                    </ConfigProvider>
                </LocaleProvider>
            </RegionContext.Provider>
        </PageContext.Provider>
    );
};

export default observer(PageProvider);
